/**
 * Inline line-item override editor. Required: a non-empty reason.
 * On save the API recomputes the snapshot; the parent passes the new
 * snapshot down so the table rerenders with the new totals.
 */

import { useState } from "react";

import { overrideLineItem } from "@starz/shared/api/sdk.gen";
import type { EstimateSnapshot, LineItem } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
  line: LineItem;
  onSaved: (snapshot: EstimateSnapshot) => void;
  onCancel: () => void;
}

export function LineItemEditor({ estimateId, line, onSaved, onCancel }: Props) {
  const [unitPrice, setUnitPrice] = useState((line.unit_price_cents / 100).toFixed(2));
  const [qtyValue, setQtyValue] = useState(line.qty_value != null ? String(line.qty_value) : "");
  const [qtyLow, setQtyLow] = useState(line.qty_low != null ? String(line.qty_low) : "");
  const [qtyHigh, setQtyHigh] = useState(line.qty_high != null ? String(line.qty_high) : "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSave() {
    if (!reason.trim()) {
      setError("Reason is required.");
      return;
    }
    setBusy(true);
    setError(null);
    const body: {
      unit_price_cents?: number;
      qty_low?: number;
      qty_value?: number;
      qty_high?: number;
      reason: string;
    } = { reason: reason.trim() };
    const newPrice = Math.round(parseFloat(unitPrice) * 100);
    if (!Number.isNaN(newPrice) && newPrice !== line.unit_price_cents) {
      body.unit_price_cents = newPrice;
    }
    const parseQ = (s: string): number | undefined => {
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : undefined;
    };
    const parsedLow = parseQ(qtyLow);
    const parsedValue = parseQ(qtyValue);
    const parsedHigh = parseQ(qtyHigh);
    if (parsedLow !== undefined && parsedLow !== line.qty_low) body.qty_low = parsedLow;
    if (parsedValue !== undefined && parsedValue !== line.qty_value) body.qty_value = parsedValue;
    if (parsedHigh !== undefined && parsedHigh !== line.qty_high) body.qty_high = parsedHigh;
    const result = await overrideLineItem({
      estimateId,
      lineId: line.line_id,
      body,
    });
    setBusy(false);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    onSaved(result.data);
  }

  return (
    <div className="space-y-3 rounded-lg border border-starz-gold/30 bg-starz-cream/30 p-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Field label={`Unit price ($/${line.unit})`}>
          <input
            type="number"
            step="0.01"
            min="0"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            className="w-full rounded border border-starz-gold/30 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Qty low">
          <input
            type="number"
            step="0.01"
            value={qtyLow}
            onChange={(e) => setQtyLow(e.target.value)}
            className="w-full rounded border border-starz-gold/30 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Qty value">
          <input
            type="number"
            step="0.01"
            value={qtyValue}
            onChange={(e) => setQtyValue(e.target.value)}
            className="w-full rounded border border-starz-gold/30 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Qty high">
          <input
            type="number"
            step="0.01"
            value={qtyHigh}
            onChange={(e) => setQtyHigh(e.target.value)}
            className="w-full rounded border border-starz-gold/30 px-3 py-2 text-sm"
          />
        </Field>
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-starz-charcoal">
          Reason (required)
        </label>
        <textarea
          rows={2}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Site visit confirmed 220 lf instead of 200 lf"
          className="mt-1 w-full rounded border border-starz-gold/30 p-3 text-sm"
        />
      </div>
      {error && (
        <p className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-2 text-sm font-semibold uppercase tracking-wide text-starz-off-white disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save override"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="min-h-[44px] rounded-lg border border-starz-gold/30 px-5 py-2 text-sm text-starz-charcoal disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-widest text-starz-charcoal">
        {props.label}
      </label>
      <div className="mt-1">{props.children}</div>
    </div>
  );
}
